import { io, type Socket } from 'socket.io-client';

import { MULTIVERSE_SOCKET_URL } from '@/const/environments.constants';
import { socketSettings } from './socket.settings';
import { SOCKET_EVENTS } from './socket.constants';

let socketInstance: Socket | null = null;

export const connectSocket = async (): Promise<Socket> => {
    if (socketInstance?.connected) {
        return socketInstance;
    }

    const baseUrl = MULTIVERSE_SOCKET_URL;

    socketInstance = io(`${baseUrl}`, socketSettings);

    socketInstance.on(SOCKET_EVENTS.CONNECT, () => {
        console.log('[Socket] Connected:', socketInstance?.id);
    });

    socketInstance.on(SOCKET_EVENTS.DISCONNECT, () => {
        console.log('[Socket] Disconnected');
    });

    socketInstance.on(SOCKET_EVENTS.ERROR, (error: Error) => {
        console.error('[Socket] Error:', error);
    });

    socketInstance.on(SOCKET_EVENTS.RECONNECT_FAILED, () => {
        console.error('[Socket] Maximum reconnection attempts reached. Will not try to reconnect.');
    });

    socketInstance.on(SOCKET_EVENTS.RECONNECT_ATTEMPT, (attempt: number) => {
        console.log(`[Socket] Reconnection attempt #${attempt}`);
    });

    return socketInstance;
};

export const disconnectSocket = (): void => {
    if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
        console.log('[Socket] Disconnected and cleaned up');
    }
};

export const getSocket = (): Socket | null => socketInstance;

export const reconnectSocket = (): void => {
    if (socketInstance && !socketInstance.connected) {
        console.log('[Socket] Force reconnecting...');
        socketInstance.connect();
    }
};

export const emit = (event: string, data?: unknown): void => {
    if (!socketInstance?.connected) {
        console.warn('[Socket] Cannot emit, socket not connected. Attempting to reconnect...');
        reconnectSocket();
        return;
    }
    socketInstance.emit(event, data);
};

export const on = <T extends Record<string, unknown> = Record<string, unknown>>(event: string, callback: (data: T) => void): void => {
    if (!socketInstance) {
        return;
    }
    socketInstance.on(event, callback);
};

export const off = (event: string, callback?: (...args: unknown[]) => void): void => {
    if (!socketInstance) return;
    socketInstance.off(event, callback);
};
